import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Person,
  Delete,
  Visibility,
  Close,
  Restore,
  Search as SearchIcon,
  Clear as ClearIcon,
  FileDownload,
  Email as EmailIcon,
  Apple as AppleIcon,
} from '@mui/icons-material';
import GoogleIcon from '@mui/icons-material/Google';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useGetUsersQuery, useVerifyUserMutation, useDeleteUserMutation, useRestoreUserMutation, useGetUserQuery, exportUsersCsv } from '../../store/api/adminApi';

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.token);
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // CSV出力関連
  const [csvDialog, setCsvDialog] = useState(false);
  const [csvExportType, setCsvExportType] = useState<'all' | 'seller' | 'buyer'>('all');
  const [csvExporting, setCsvExporting] = useState(false);
  const [csvError, setCsvError] = useState('');

  const handleCsvExport = async () => {
    if (!token) {
      setCsvError('認証トークンがありません。再ログインしてください。');
      return;
    }
    setCsvExporting(true);
    setCsvError('');
    try {
      await exportUsersCsv(csvExportType, token);
      setCsvDialog(false);
    } catch (error: any) {
      console.error('CSV export error:', error);
      setCsvError('CSVエクスポートに失敗しました。');
    } finally {
      setCsvExporting(false);
    }
  };
  
  // URLパラメータから検索クエリを初期化
  const initialSearchQuery = searchParams.get('search') || '';
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialSearchQuery);
  
  // URLからステータスフィルターを読み取る
  const statusFilter = useMemo(() => {
    return searchParams.get('status') || 'all';
  }, [searchParams]);
  
  // ステータスフィルターを変更する関数
  const setStatusFilter = (value: string) => {
    if (value === 'all') {
      searchParams.delete('status');
    } else {
      searchParams.set('status', value);
    }
    setSearchParams(searchParams);
  };
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [verificationDialog, setVerificationDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [userDetailDialog, setUserDetailDialog] = useState(false);
  const [userDetailTab, setUserDetailTab] = useState(0);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [restoreReason, setRestoreReason] = useState('');
  const [imagePreviewDialog, setImagePreviewDialog] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [isViewOnlyMode, setIsViewOnlyMode] = useState(false);
  
  // 検索クエリのデバウンス処理
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(0); // 検索時はページをリセット
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ステータスフィルタをAPIクエリパラメータに変換する関数  
  const getQueryParams = () => {
    const params: any = {
      page: page + 1,
      limit: rowsPerPage,
    };
    
    // 検索クエリを追加
    if (debouncedSearchQuery) {
      params.search = debouncedSearchQuery;
      console.log('Setting search:', debouncedSearchQuery);
    }
    
    console.log('Current statusFilter:', statusFilter);
    
    switch (statusFilter) {
      case 'verified':
        params.verified = 'true';
        console.log('Setting verified=true');
        break;
      case 'unverified':
        params.verified = 'false';
        console.log('Setting verified=false');
        break;
      case 'pending_verification':
        params.verificationStatus = 'pending';
        console.log('Setting verificationStatus=pending');
        break;
      case 'rejected':
        params.verificationStatus = 'rejected';
        console.log('Setting verificationStatus=rejected');
        break;
      case 'deleted':
        params.status = 'deleted';
        console.log('Setting status=deleted');
        break;
      default:
        console.log('No filter applied (all users)');
        break;
    }
    
    console.log('Query params being sent:', params);
    return params;
  };

  const queryParams = getQueryParams();
  console.log('About to call useGetUsersQuery with params:', queryParams);
  console.log('Params type check:', typeof queryParams, Array.isArray(queryParams));
  
  const { data: usersData, isLoading, error, refetch } = useGetUsersQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  });

  // 選択されたユーザーの詳細情報を取得
  const { data: selectedUserDetails, isLoading: isLoadingUserDetails } = useGetUserQuery(
    selectedUser?.id || '',
    {
      skip: !selectedUser?.id,
    }
  );

  const [verifyUser] = useVerifyUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [restoreUser] = useRestoreUserMutation();

  // フィルタやページ変更時に強制的にrefetch
  useEffect(() => {
    console.log('Filter or page changed, forcing refetch');
    refetch();
  }, [statusFilter, page, rowsPerPage, debouncedSearchQuery, refetch]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (value: string) => {
    console.log('Status filter changed to:', value);
    setStatusFilter(value);
    setPage(0); // フィルタ変更時はページをリセット
  };
  
  // URLパラメータが変更されたらページをリセット
  useEffect(() => {
    setPage(0);
  }, [statusFilter]);

  const handleVerifyUser = async (approved: boolean) => {
    if (!selectedUser) return;

    try {
      await verifyUser({
        id: selectedUser.id,
        approved,
        reason: verificationNotes
      }).unwrap();
      
      setVerificationDialog(false);
      setSelectedUser(null);
      setVerificationNotes('');
      setIsViewOnlyMode(false);
      refetch();
    } catch (error) {
      console.error('Verification failed:', error);
    }
  };

  const handleCloseVerificationDialog = () => {
    setVerificationDialog(false);
    setSelectedUser(null);
    setVerificationNotes('');
    setIsViewOnlyMode(false);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser || !deleteReason.trim()) return;

    try {
      await deleteUser({ 
        id: selectedUser.id, 
        reason: deleteReason.trim() 
      }).unwrap();
      
      setDeleteDialog(false);
      setSelectedUser(null);
      setDeleteReason('');
      refetch();
    } catch (error) {
      console.error('Delete failed:', error);
      alert('削除に失敗しました。');
    }
  };

  const handleRestoreUser = async () => {
    if (!selectedUser || !restoreReason.trim()) return;

    try {
      await restoreUser({ 
        id: selectedUser.id, 
        reason: restoreReason.trim() 
      }).unwrap();
      
      setRestoreDialog(false);
      setSelectedUser(null);
      setRestoreReason('');
      refetch();
    } catch (error) {
      console.error('Restore failed:', error);
      alert('復元に失敗しました。');
    }
  };

  const handleImagePreview = (imageUrl: string) => {
    setPreviewImageUrl(imageUrl);
    setImagePreviewDialog(true);
  };

  const getAuthProviderChips = (providers: string[] = []) => {
    if (providers.length === 0) return <Typography variant="caption" color="text.secondary">不明</Typography>;
    return (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        {providers.map((provider) => {
          switch (provider) {
            case 'password':
              return <Chip key={provider} label="メール" size="small" icon={<EmailIcon />} variant="outlined" sx={{ fontSize: '0.7rem' }} />;
            case 'apple.com':
              return <Chip key={provider} label="Apple" size="small" icon={<AppleIcon />} variant="outlined" sx={{ fontSize: '0.7rem' }} />;
            case 'google.com':
              return <Chip key={provider} label="Google" size="small" icon={<GoogleIcon />} variant="outlined" sx={{ fontSize: '0.7rem' }} />;
            default:
              return <Chip key={provider} label={provider} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
          }
        })}
      </Box>
    );
  };

  const getStatusChip = (user: any) => {
    if (user.status === 'DELETED') {
      return <Chip label="削除済み" color="error" size="small" icon={<Delete />} />;
    } else if (user.isVerified) {
      return <Chip label="本人確認済み" color="success" size="small" icon={<CheckCircle />} />;
    } else if (user.verificationStatus === 'rejected') {
      return <Chip label="拒否済み" color="error" size="small" icon={<Cancel />} />;
    } else if (user.verificationSubmitted) {
      return <Chip label="審査待ち" color="warning" size="small" icon={<Warning />} />;
    }
    return <Chip label="未確認" color="default" size="small" icon={<Person />} />;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        ユーザーデータの読み込みに失敗しました
      </Alert>
    );
  }

  const users = usersData?.items || [];
  const totalUsers = usersData?.totalCount || 0;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        ユーザー管理
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="ユーザー名、メール、IDで検索"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 300 }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>ステータス</InputLabel>
          <Select
            value={statusFilter}
            label="ステータス"
            onChange={(e) => handleStatusFilterChange(e.target.value as string)}
          >
            <MenuItem value="all">全て</MenuItem>
            <MenuItem value="unverified">未確認</MenuItem>
            <MenuItem value="pending_verification">審査待ち</MenuItem>
            <MenuItem value="verified">本人確認済み</MenuItem>
            <MenuItem value="rejected">拒否済み</MenuItem>
            <MenuItem value="deleted">削除済み</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ ml: 'auto' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownload />}
            onClick={() => {
              setCsvDialog(true);
              setCsvError('');
            }}
          >
            CSV出力
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ユーザー</TableCell>
              <TableCell>メールアドレス</TableCell>
              <TableCell>登録方法</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>登録日</TableCell>
              <TableCell>アクション</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user: any) => {
              const isDeleted = user.status === 'DELETED';
              return (
                <TableRow 
                  key={user.id}
                  sx={{ 
                    backgroundColor: isDeleted ? 'rgba(0, 0, 0, 0.04)' : 'inherit',
                    opacity: isDeleted ? 0.7 : 1
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Avatar src={user.profileImage} sx={{ mr: 2 }}>
                        {user.name?.[0] || user.email?.[0]?.toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {user.name || '名前未設定'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {user.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getAuthProviderChips(user.authProviders)}</TableCell>
                  <TableCell>{getStatusChip(user)}</TableCell>
                  <TableCell>
                    {new Date(user.createdAt).toLocaleDateString('ja-JP')}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Button
                        size="small"
                        variant="outlined"
                        color="info"
                        startIcon={<Visibility />}
                        onClick={() => {
                          setSelectedUser(user);
                          setUserDetailDialog(true);
                        }}
                      >
                        詳細
                      </Button>
                      {isDeleted ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<Restore />}
                          onClick={() => {
                            setSelectedUser(user);
                            setRestoreDialog(true);
                          }}
                        >
                          復元
                        </Button>
                      ) : (
                        <>
                          {user.verificationSubmitted && (
                            <>
                              {user.isVerified ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="info"
                                  startIcon={<Visibility />}
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setIsViewOnlyMode(true);
                                    setVerificationDialog(true);
                                  }}
                                >
                                  確認
                                </Button>
                              ) : user.verificationStatus === 'rejected' ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setIsViewOnlyMode(false);
                                    setVerificationDialog(true);
                                  }}
                                >
                                  再審査
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setIsViewOnlyMode(false);
                                    setVerificationDialog(true);
                                  }}
                                >
                                  審査
                                </Button>
                              )}
                            </>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => {
                              setSelectedUser(user);
                              setDeleteDialog(true);
                            }}
                          >
                            削除
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* 本人確認審査ダイアログ */}
      <Dialog open={verificationDialog} onClose={handleCloseVerificationDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isViewOnlyMode ? '本人確認書類確認' : '本人確認審査'}</DialogTitle>
        <DialogContent>
          {isLoadingUserDetails ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {selectedUserDetails?.status === 'DELETED' && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight="bold">
                    このユーザーは削除されています
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    削除理由: {selectedUserDetails?.deletionReason || 'N/A'}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    削除日時: {selectedUserDetails?.deletedAt ? new Date(selectedUserDetails.deletedAt).toLocaleString('ja-JP') : 'N/A'}
                  </Typography>
                </Alert>
              )}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  ユーザー情報
                </Typography>
                <Typography variant="body2" gutterBottom>
                  ユーザー: {selectedUserDetails?.name || selectedUserDetails?.email}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  メールアドレス: {selectedUserDetails?.email}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  登録日: {selectedUserDetails?.createdAt ? new Date(selectedUserDetails.createdAt).toLocaleDateString('ja-JP') : 'N/A'}
                </Typography>
              </Box>

              {/* 住所情報 */}
              {selectedUserDetails?.address && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    住所情報
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    都道府県: {selectedUserDetails.address.adminArea || 'N/A'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    市区町村: {selectedUserDetails.address.city || 'N/A'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    住所: {selectedUserDetails.address.street || 'N/A'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    郵便番号: {selectedUserDetails.address.postalCode || 'N/A'}
                  </Typography>
                </Box>
              )}

              {/* 本人確認書類 */}
              {selectedUserDetails?.documentation && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    本人確認書類
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    姓: {selectedUserDetails.documentation.lastName || 'N/A'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    名: {selectedUserDetails.documentation.firstName || 'N/A'}
                  </Typography>
                  {/* 書類種類 */}
                  <Typography variant="body2" gutterBottom>
                    書類種類: {
                      selectedUserDetails.documentation.documentType === 'driver_license' ? '運転免許証' :
                      selectedUserDetails.documentation.documentType === 'my_number_card' ? 'マイナンバーカード' :
                      selectedUserDetails.documentation.documentType === 'passport' ? 'パスポート' :
                      selectedUserDetails.documentation.documentType || 'N/A'
                    }
                  </Typography>
                  {selectedUserDetails.documentation.document && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        {selectedUserDetails.documentation.documentType === 'driver_license' ? '本人確認書類画像（表面）:' : '本人確認書類画像:'}
                      </Typography>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          p: 1,
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          cursor: 'pointer'
                        }}
                        onClick={() => handleImagePreview(selectedUserDetails.documentation.document)}
                      >
                        <img 
                          src={selectedUserDetails.documentation.document} 
                          alt="本人確認書類（表面）" 
                          style={{ 
                            width: '100px', 
                            height: '60px', 
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                        <Box>
                          <Typography variant="body2">
                            {selectedUserDetails.documentation.documentType === 'driver_license' ? '本人確認書類（表面）' : '本人確認書類'}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImagePreview(selectedUserDetails.documentation.document);
                            }}
                          >
                            プレビュー
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  {/* 運転免許証の場合のみ裏面を表示 */}
                  {selectedUserDetails.documentation.documentType === 'driver_license' && selectedUserDetails.documentation.documentBack && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        本人確認書類画像（裏面）:
                      </Typography>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          p: 1,
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          cursor: 'pointer'
                        }}
                        onClick={() => handleImagePreview(selectedUserDetails.documentation.documentBack)}
                      >
                        <img 
                          src={selectedUserDetails.documentation.documentBack} 
                          alt="本人確認書類（裏面）" 
                          style={{ 
                            width: '100px', 
                            height: '60px', 
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                        <Box>
                          <Typography variant="body2">
                            本人確認書類（裏面）
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<Visibility />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImagePreview(selectedUserDetails.documentation.documentBack);
                            }}
                          >
                            プレビュー
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  )}
                  {/* 運転免許証で裏面がない場合の警告 */}
                  {selectedUserDetails.documentation.documentType === 'driver_license' && !selectedUserDetails.documentation.documentBack && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      運転免許証の裏面画像がありません
                    </Alert>
                  )}
                </Box>
              )}

              {/* 審査メモ */}
              {!isViewOnlyMode && (
                <Box sx={{ mb: 2 }}>
                  {selectedUserDetails?.verificationStatus === 'rejected' && selectedUserDetails?.verificationRejectionReason && (
                    <Box sx={{ mb: 2 }}>
                      <Alert severity="warning">
                        <Typography variant="body2" fontWeight="bold">
                          前回の拒否理由:
                        </Typography>
                        <Typography variant="body2">
                          {selectedUserDetails.verificationRejectionReason}
                        </Typography>
                      </Alert>
                    </Box>
                  )}
                  <TextField
                    autoFocus
                    margin="dense"
                    label={selectedUserDetails?.verificationStatus === 'rejected' ? "再審査メモ" : "審査メモ"}
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={verificationNotes}
                    onChange={(e) => setVerificationNotes(e.target.value)}
                    placeholder="承認/拒否の理由を入力してください"
                    multiline
                    rows={4}
                  />
                </Box>
              )}
              
              {/* 確認モード時の審査履歴表示 */}
              {isViewOnlyMode && selectedUserDetails?.verificationStatus === 'rejected' && selectedUserDetails?.verificationRejectionReason && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="info">
                    <Typography variant="body2" fontWeight="bold">
                      前回の拒否理由:
                    </Typography>
                    <Typography variant="body2">
                      {selectedUserDetails.verificationRejectionReason}
                    </Typography>
                  </Alert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVerificationDialog}>
            {isViewOnlyMode ? '閉じる' : 'キャンセル'}
          </Button>
          {!isViewOnlyMode && (
            <>
              <Button onClick={() => handleVerifyUser(false)} color="error">
                拒否
              </Button>
              <Button onClick={() => handleVerifyUser(true)} color="success" variant="contained">
                承認
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ユーザー削除ダイアログ */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ユーザー削除</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              ユーザー: {selectedUser?.name || selectedUser?.email}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              この操作は論理削除です。後で復元できます。
            </Alert>
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="削除理由"
            type="text"
            fullWidth
            variant="outlined"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="削除理由を入力してください"
            multiline
            rows={4}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error" 
            variant="contained"
            disabled={!deleteReason.trim()}
          >
            削除実行
          </Button>
        </DialogActions>
      </Dialog>

      {/* ユーザー復元ダイアログ */}
      <Dialog open={restoreDialog} onClose={() => setRestoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>ユーザー復元</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              ユーザー: {selectedUser?.name || selectedUser?.email}
            </Typography>
            {selectedUser?.deletionReason && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  削除理由:
                </Typography>
                <Typography variant="body2">
                  {selectedUser.deletionReason}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  削除日時: {selectedUser.deletedAt ? new Date(selectedUser.deletedAt).toLocaleString('ja-JP') : 'N/A'}
                </Typography>
              </Alert>
            )}
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label="復元理由"
            type="text"
            fullWidth
            variant="outlined"
            value={restoreReason}
            onChange={(e) => setRestoreReason(e.target.value)}
            placeholder="復元理由を入力してください"
            multiline
            rows={4}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleRestoreUser} 
            color="success" 
            variant="contained"
            disabled={!restoreReason.trim()}
          >
            復元実行
          </Button>
        </DialogActions>
      </Dialog>

      {/* 画像プレビューダイアログ */}
      <Dialog
        open={imagePreviewDialog}
        onClose={() => setImagePreviewDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          本人確認書類
          <Button
            onClick={() => setImagePreviewDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </Button>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            {previewImageUrl && (
              <img
                src={previewImageUrl}
                alt="本人確認書類"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                }}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>
      {/* CSV出力ダイアログ */}
      <Dialog open={csvDialog} onClose={() => setCsvDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>CSV出力（広告配信用顧客リスト）</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">出力対象</FormLabel>
              <RadioGroup
                value={csvExportType}
                onChange={(e) => setCsvExportType(e.target.value as 'all' | 'seller' | 'buyer')}
              >
                <FormControlLabel value="all" control={<Radio />} label="全ユーザー" />
                <FormControlLabel value="seller" control={<Radio />} label="出品者（本人確認済みユーザー）" />
                <FormControlLabel value="buyer" control={<Radio />} label="購入者（本人確認未済ユーザー）" />
              </RadioGroup>
            </FormControl>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>出力CSV列:</strong> email, phone, country, fn(名), ln(姓), zip(郵便番号), gen(性別), age(年齢), dob(生年月日), user_type(ユーザー種別)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ※ phone, gen, age は現在ユーザーデータに保存されていないため空欄です。
              zip, dob は本人確認書類から取得可能な場合のみ出力されます。
            </Typography>
          </Alert>
          {csvError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {csvError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvDialog(false)} disabled={csvExporting}>
            キャンセル
          </Button>
          <Button
            onClick={handleCsvExport}
            variant="contained"
            color="primary"
            disabled={csvExporting}
            startIcon={csvExporting ? <CircularProgress size={20} /> : <FileDownload />}
          >
            {csvExporting ? 'エクスポート中...' : 'ダウンロード'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ユーザー詳細ダイアログ */}
      <Dialog
        open={userDetailDialog}
        onClose={() => { setUserDetailDialog(false); setUserDetailTab(0); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          ユーザー詳細情報
          <IconButton
            onClick={() => { setUserDetailDialog(false); setUserDetailTab(0); }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingUserDetails ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : selectedUserDetails ? (
            <Box>
              {/* 基本情報ヘッダー */}
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  src={selectedUserDetails.profileImage}
                  sx={{ width: 80, height: 80 }}
                >
                  <Person sx={{ fontSize: 40 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedUserDetails.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    ID: {selectedUserDetails.id}
                  </Typography>
                </Box>
              </Box>

              {/* タブ */}
              <Tabs
                value={userDetailTab}
                onChange={(_, v) => setUserDetailTab(v)}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="基本情報" />
                <Tab label={`出品商品 (${(selectedUserDetails as any).listedProducts?.length || 0})`} />
                <Tab label={`購入商品 (${(selectedUserDetails as any).purchasedProducts?.length || 0})`} />
              </Tabs>

              {/* タブ0: 基本情報 */}
              {userDetailTab === 0 && (<Box>
              {/* アカウント情報 */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2, borderBottom: '1px solid #eee', pb: 0.5 }}>
                アカウント情報
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">メールアドレス</Typography>
                <Typography variant="body2">{selectedUserDetails.email}</Typography>

                <Typography variant="body2" color="text.secondary">登録方法</Typography>
                <Box>{getAuthProviderChips(selectedUserDetails.authProviders)}</Box>

                <Typography variant="body2" color="text.secondary">登録日時</Typography>
                <Typography variant="body2">
                  {selectedUserDetails.createdAt ? new Date(selectedUserDetails.createdAt).toLocaleString('ja-JP') : '未設定'}
                </Typography>

                <Typography variant="body2" color="text.secondary">最終ログイン</Typography>
                <Typography variant="body2">
                  {selectedUserDetails.lastLoginAt ? new Date(selectedUserDetails.lastLoginAt).toLocaleString('ja-JP') : '未設定'}
                </Typography>

                <Typography variant="body2" color="text.secondary">ステータス</Typography>
                <Typography variant="body2">
                  {selectedUserDetails.status === 'DELETED' ? (
                    <Chip label="削除済み" size="small" color="error" />
                  ) : (
                    <Chip label="アクティブ" size="small" color="success" />
                  )}
                </Typography>

                <Typography variant="body2" color="text.secondary">プロフィール</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedUserDetails.description || '未設定'}
                </Typography>
              </Box>

              {/* 本人確認情報 */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2, borderBottom: '1px solid #eee', pb: 0.5 }}>
                本人確認情報
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">本人確認状態</Typography>
                <Typography variant="body2">
                  {selectedUserDetails.isVerified ? (
                    <Chip label="確認済み" size="small" color="success" icon={<CheckCircle />} />
                  ) : selectedUserDetails.verificationSubmitted ? (
                    <Chip label="審査待ち" size="small" color="warning" icon={<Warning />} />
                  ) : (
                    <Chip label="未提出" size="small" color="default" />
                  )}
                </Typography>

                {selectedUserDetails.verificationSubmitted && (
                  <>
                    <Typography variant="body2" color="text.secondary">審査ステータス</Typography>
                    <Typography variant="body2">{selectedUserDetails.verificationStatus}</Typography>
                  </>
                )}

                {selectedUserDetails.verificationReviewedAt && (
                  <>
                    <Typography variant="body2" color="text.secondary">審査日時</Typography>
                    <Typography variant="body2">
                      {new Date(selectedUserDetails.verificationReviewedAt).toLocaleString('ja-JP')}
                    </Typography>
                  </>
                )}

                {selectedUserDetails.verificationRejectionReason && (
                  <>
                    <Typography variant="body2" color="text.secondary">拒否理由</Typography>
                    <Typography variant="body2" color="error">{selectedUserDetails.verificationRejectionReason}</Typography>
                  </>
                )}

                {selectedUserDetails.verificationNotes && (
                  <>
                    <Typography variant="body2" color="text.secondary">審査メモ</Typography>
                    <Typography variant="body2">{selectedUserDetails.verificationNotes}</Typography>
                  </>
                )}
              </Box>

              {/* 住所情報 */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2, borderBottom: '1px solid #eee', pb: 0.5 }}>
                住所情報
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">郵便番号</Typography>
                <Typography variant="body2">{selectedUserDetails.address?.postalCode || '未設定'}</Typography>

                <Typography variant="body2" color="text.secondary">都道府県</Typography>
                <Typography variant="body2">{selectedUserDetails.address?.prefecture || selectedUserDetails.address?.adminArea || '未設定'}</Typography>

                <Typography variant="body2" color="text.secondary">市区町村</Typography>
                <Typography variant="body2">{selectedUserDetails.address?.city || '未設定'}</Typography>

                <Typography variant="body2" color="text.secondary">番地・建物名</Typography>
                <Typography variant="body2">{selectedUserDetails.address?.street || '未設定'}</Typography>
              </Box>

              {/* 管理情報 */}
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2, borderBottom: '1px solid #eee', pb: 0.5 }}>
                管理情報
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 1, mb: 2 }}>
                <Typography variant="body2" color="text.secondary">BAN状態</Typography>
                <Typography variant="body2">
                  {selectedUserDetails.isBanned ? (
                    <Chip label="BAN中" size="small" color="error" />
                  ) : (
                    <Chip label="正常" size="small" color="success" />
                  )}
                </Typography>

                {selectedUserDetails.isBanned && selectedUserDetails.banReason && (
                  <>
                    <Typography variant="body2" color="text.secondary">BAN理由</Typography>
                    <Typography variant="body2" color="error">{selectedUserDetails.banReason}</Typography>
                  </>
                )}

                <Typography variant="body2" color="text.secondary">購入ブロック</Typography>
                <Typography variant="body2">
                  {selectedUserDetails.purchaseBlocked ? (
                    <Chip label="ブロック中" size="small" color="error" />
                  ) : (
                    <Chip label="正常" size="small" color="success" />
                  )}
                </Typography>

                {selectedUserDetails.purchaseBlocked && selectedUserDetails.purchaseBlockReason && (
                  <>
                    <Typography variant="body2" color="text.secondary">ブロック理由</Typography>
                    <Typography variant="body2" color="error">{selectedUserDetails.purchaseBlockReason}</Typography>
                  </>
                )}

                {selectedUserDetails.adminNotes && (
                  <>
                    <Typography variant="body2" color="text.secondary">管理メモ</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedUserDetails.adminNotes}</Typography>
                  </>
                )}
              </Box>

              {/* 削除情報 */}
              {selectedUserDetails.status === 'DELETED' && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2, borderBottom: '1px solid #eee', pb: 0.5 }}>
                    削除情報
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 1, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">削除日時</Typography>
                    <Typography variant="body2">
                      {selectedUserDetails.deletedAt ? new Date(selectedUserDetails.deletedAt).toLocaleString('ja-JP') : '不明'}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">削除理由</Typography>
                    <Typography variant="body2">{selectedUserDetails.deletionReason || '理由なし'}</Typography>
                  </Box>
                </>
              )}

              {/* 本人確認書類 */}
              {selectedUserDetails.documentation && (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, mt: 2, borderBottom: '1px solid #eee', pb: 0.5 }}>
                    本人確認書類
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {/* 書類に含まれる情報 */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 1, mb: 2 }}>
                      {selectedUserDetails.documentation.name && (
                        <>
                          <Typography variant="body2" color="text.secondary">書類上の氏名</Typography>
                          <Typography variant="body2">{selectedUserDetails.documentation.name}</Typography>
                        </>
                      )}
                      {selectedUserDetails.documentation.birthday && (
                        <>
                          <Typography variant="body2" color="text.secondary">生年月日</Typography>
                          <Typography variant="body2">{selectedUserDetails.documentation.birthday}</Typography>
                        </>
                      )}
                      {selectedUserDetails.documentation.documentType && (
                        <>
                          <Typography variant="body2" color="text.secondary">書類種類</Typography>
                          <Typography variant="body2">{selectedUserDetails.documentation.documentType}</Typography>
                        </>
                      )}
                    </Box>

                    {/* 書類画像 */}
                    {(() => {
                      const docs = selectedUserDetails.documentation;
                      const imageUrls: string[] = [];
                      if (docs.document) imageUrls.push(docs.document);
                      if (docs.documentBack) imageUrls.push(docs.documentBack);
                      if (docs.frontUrl) imageUrls.push(docs.frontUrl);
                      if (docs.backUrl) imageUrls.push(docs.backUrl);
                      if (docs.imageUrl) imageUrls.push(docs.imageUrl);
                      if (Array.isArray(docs.images)) imageUrls.push(...docs.images);

                      if (imageUrls.length === 0) return null;
                      return (
                        <Box display="flex" gap={2} flexWrap="wrap" mt={1}>
                          {imageUrls.map((url: string, idx: number) => (
                            <Box
                              key={idx}
                              sx={{ cursor: 'pointer', border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}
                              onClick={() => {
                                setPreviewImageUrl(url);
                                setImagePreviewDialog(true);
                              }}
                            >
                              <img src={url} alt={`書類 ${idx + 1}`} style={{ width: 120, height: 80, objectFit: 'cover' }} />
                            </Box>
                          ))}
                        </Box>
                      );
                    })()}
                  </Box>
                </>
              )}
              </Box>)}

              {/* タブ1: 出品商品 */}
              {userDetailTab === 1 && (
                <Box>
                  {(selectedUserDetails as any).listedProducts?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>画像</TableCell>
                            <TableCell>商品名</TableCell>
                            <TableCell>価格</TableCell>
                            <TableCell>ステータス</TableCell>
                            <TableCell>出品日</TableCell>
                            <TableCell>操作</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(selectedUserDetails as any).listedProducts.map((product: any) => (
                            <TableRow key={product.id} hover>
                              <TableCell>
                                <Avatar src={product.images?.[0] || ''} variant="rounded" sx={{ width: 40, height: 40 }} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">{product.productName}</Typography>
                                <Typography variant="caption" color="text.secondary">ID: {product.id?.substring(0, 10)}...</Typography>
                              </TableCell>
                              <TableCell>¥{product.price?.toLocaleString()}</TableCell>
                              <TableCell>
                                {product.buyer ? (
                                  <Chip label="販売済み" size="small" sx={{ backgroundColor: '#7b1fa2', color: 'white' }} />
                                ) : product.status === 'ACTIVE' ? (
                                  <Chip label="出品中" size="small" color="success" />
                                ) : product.status === 'PENDING' ? (
                                  <Chip label="審査中" size="small" color="warning" />
                                ) : product.status === 'REJECTED' ? (
                                  <Chip label="却下" size="small" color="error" />
                                ) : product.status === 'DELETED' ? (
                                  <Chip label="削除済" size="small" color="default" />
                                ) : (
                                  <Chip label={product.status || '不明'} size="small" />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {product.createdAt ? new Date(product.createdAt).toLocaleDateString('ja-JP') : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    navigate(`/products?search=${product.id}`);
                                    setUserDetailDialog(false);
                                    setUserDetailTab(0);
                                  }}
                                >
                                  詳細
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" textAlign="center" py={4}>出品商品はありません</Typography>
                  )}
                </Box>
              )}

              {/* タブ2: 購入商品 */}
              {userDetailTab === 2 && (
                <Box>
                  {(selectedUserDetails as any).purchasedProducts?.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>画像</TableCell>
                            <TableCell>商品名</TableCell>
                            <TableCell>価格</TableCell>
                            <TableCell>ステータス</TableCell>
                            <TableCell>操作</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(selectedUserDetails as any).purchasedProducts.map((product: any) => (
                            <TableRow key={product.id} hover>
                              <TableCell>
                                <Avatar src={product.images?.[0] || ''} variant="rounded" sx={{ width: 40, height: 40 }} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="bold">{product.productName}</Typography>
                                <Typography variant="caption" color="text.secondary">ID: {product.id?.substring(0, 10)}...</Typography>
                              </TableCell>
                              <TableCell>¥{product.price?.toLocaleString()}</TableCell>
                              <TableCell>
                                <Chip label="購入済み" size="small" sx={{ backgroundColor: '#7b1fa2', color: 'white' }} />
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    navigate(`/products?search=${product.id}`);
                                    setUserDetailDialog(false);
                                    setUserDetailTab(0);
                                  }}
                                >
                                  詳細
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" textAlign="center" py={4}>購入商品はありません</Typography>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">ユーザー情報を取得できませんでした。</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUserDetailDialog(false); setUserDetailTab(0); }}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage; 